import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { Fragment, useState } from 'react';

import { CategoryBarChart } from '@apps/web/components/admin/org_distributions/category_bar_chart';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    useAllAdminOrganizations,
    usePromptQuality,
    usePromptRollout,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type CategoryCount,
    type PromptRolloutEntry,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/prompt-rollout')({
    head: () => ({
        meta: [
            {
                title: 'Prompt Rollout | Phoebe Admin',
            },
        ],
    }),
    component: PromptRolloutPage,
});

const NO_TEST_VERSION = 'none';

async function putStringFlag(flagName: string, value: string) {
    await getApiClient().put({
        url: '/admin/admin-flags/{flag_name}',
        pathParams: { flag_name: flagName },
        body: { type: 'string', value },
    });
}

async function putTestOrgsFlag(flagName: string, orgIds: Array<string>) {
    await getApiClient().put({
        url: '/admin/admin-flags/{flag_name}',
        pathParams: { flag_name: flagName },
        body: { type: 'list-uuid', value: orgIds },
    });
}

async function promotePromptCandidate(
    promptKey: string,
    candidateVersion: string,
) {
    await getApiClient().post({
        url: '/admin/prompt-rollout/{prompt_key}/promote',
        pathParams: { prompt_key: promptKey },
        body: { candidate_version: candidateVersion },
    });
}

function StatCard({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number;
}) {
    return (
        <div className="rounded-md border p-4">
            <div className="text-muted-foreground text-xs font-medium">
                {label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
                {value}
            </div>
        </div>
    );
}

function StatusBadge({
    testVersion,
    testOrgCount,
}: {
    readonly testVersion: string | null;
    readonly testOrgCount: number;
}) {
    if (testVersion && testOrgCount > 0) {
        return (
            <Badge variant="default">
                Testing on{' '}
                {testOrgCount === 1 ? '1 org' : `${testOrgCount} orgs`}
            </Badge>
        );
    }
    if (testVersion) {
        return <Badge variant="secondary">Test version set</Badge>;
    }
    return <Badge variant="outline">All on default</Badge>;
}

function TestOrgsEditor({
    entry,
    onSaved,
}: {
    readonly entry: PromptRolloutEntry;
    readonly onSaved: () => void;
}) {
    const [search, setSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const { organizations, isLoading, isLoadingMore, isReachingEnd, setSize } =
        useAllAdminOrganizations({ search, orgDataOnly: true });

    const selectedIds = entry.candidate_orgs.map((org) => org.id);
    // Seed options with the already-selected orgs (names come from the
    // endpoint) so chips render names even before a search loads them.
    const optionMap = new Map<string, string>();
    for (const org of entry.candidate_orgs) {
        optionMap.set(org.id, org.name);
    }
    for (const org of organizations) {
        optionMap.set(org.id, org.name);
    }
    const options = Array.from(optionMap, ([value, label]) => ({
        value,
        label,
    }));

    const save = async (orgIds: Array<string>) => {
        setIsSaving(true);
        try {
            await putTestOrgsFlag(entry.candidate_org_ids_flag, orgIds);
            toast.success('Test orgs updated', { persistNotification: false });
            onSaved();
        } catch {
            toast.error('Failed to update test orgs', {
                persistNotification: false,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            <div className="text-muted-foreground mb-1 text-xs font-medium">
                Test organizations — search and select the orgs that get the
                test version. Everyone else stays on the default.
            </div>
            <MultiSelect
                className="max-w-xl"
                options={options}
                selected={selectedIds}
                onChange={(orgIds) => {
                    void save(orgIds);
                }}
                placeholder="Search organizations by name…"
                searchValue={search}
                onSearchChange={setSearch}
                isLoading={isLoading || isLoadingMore}
                canLoadMore={!isReachingEnd}
                onLoadMore={() => {
                    setSize((current) => current + 1);
                }}
                disabled={isSaving}
            />
        </div>
    );
}

const VERDICT_VARIANT = {
    safe: 'success',
    worse: 'destructive',
    pending: 'secondary',
} as const;

const MIN_RUNS_FOR_VERDICT = 50;

function formatBadRate(badRate: number | null): string {
    return badRate === null ? '—' : `${(badRate * 100).toFixed(1)}%`;
}

function QualityPanel({
    promptKey,
    defaultVersion,
    candidateVersion,
}: {
    readonly promptKey: string;
    readonly defaultVersion: string;
    readonly candidateVersion: string | null;
}) {
    const { data, isLoading } = usePromptQuality(promptKey);

    if (isLoading) {
        return (
            <div className="text-muted-foreground text-xs">
                Loading quality…
            </div>
        );
    }
    if (!data || !data.supported) {
        return (
            <div className="text-muted-foreground text-xs">
                Quality slicing is not available for this surface yet.
            </div>
        );
    }
    if (data.by_version.length === 0) {
        return (
            <div className="text-muted-foreground text-xs">
                No graded runs in the last {data.days} days yet.
            </div>
        );
    }

    const baseline = data.by_version.find(
        (quality) => quality.version === defaultVersion,
    );
    const candidate = candidateVersion
        ? data.by_version.find(
              (quality) => quality.version === candidateVersion,
          )
        : undefined;

    let verdict: {
        readonly label: string;
        readonly tone: keyof typeof VERDICT_VARIANT;
    } | null = null;
    if (candidateVersion) {
        if (!candidate || candidate.runs < MIN_RUNS_FOR_VERDICT) {
            verdict = {
                label: `Gathering data (n=${candidate?.runs ?? 0})`,
                tone: 'pending',
            };
        } else if (
            candidate.bad_rate === null ||
            !baseline ||
            baseline.bad_rate === null
        ) {
            verdict = { label: 'Gathering grades', tone: 'pending' };
        } else {
            const deltaPts = (candidate.bad_rate - baseline.bad_rate) * 100;
            const signed = `${deltaPts > 0 ? '+' : ''}${deltaPts.toFixed(1)} pts`;
            verdict =
                deltaPts <= 0.5
                    ? { label: `Safe to promote (${signed})`, tone: 'safe' }
                    : {
                          label: `Candidate worse (${signed}) — hold`,
                          tone: 'worse',
                      };
        }
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="text-muted-foreground text-xs font-medium">
                Quality — last {data.days} days, by version
            </div>
            {verdict !== null ? (
                <Badge
                    variant={VERDICT_VARIANT[verdict.tone]}
                    className="w-fit"
                >
                    {verdict.label}
                </Badge>
            ) : null}
            <div className="grid w-fit grid-cols-4 gap-x-6 gap-y-1 text-xs">
                <div className="text-muted-foreground">Version</div>
                <div className="text-muted-foreground">Runs</div>
                <div className="text-muted-foreground">Bad</div>
                <div className="text-muted-foreground">Bad rate</div>
                {data.by_version.map((quality) => (
                    <Fragment key={quality.version}>
                        <div className="font-mono">
                            {quality.version}
                            {quality.version === defaultVersion
                                ? ' · default'
                                : ''}
                            {quality.version === candidateVersion
                                ? ' · candidate'
                                : ''}
                        </div>
                        <div className="tabular-nums">{quality.runs}</div>
                        <div className="tabular-nums">{quality.bad}</div>
                        <div className="tabular-nums">
                            {formatBadRate(quality.bad_rate)}
                        </div>
                    </Fragment>
                ))}
            </div>
        </div>
    );
}

function PromptRolloutRow({
    entry,
    onSaved,
}: {
    readonly entry: PromptRolloutEntry;
    readonly onSaved: () => void;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const testVersion = entry.candidate_version;
    const manualOrgCount = entry.candidate_orgs.length;
    // Training-mode orgs auto-receive the candidate on top of the manual
    // cohort; count both so a candidate served only to training-mode orgs
    // doesn't read as "0 orgs".
    const autoOrgCount = entry.auto_test_organization_count;
    const testOrgCount = manualOrgCount + autoOrgCount;

    const runSave = async (action: () => Promise<void>) => {
        setIsSaving(true);
        try {
            await action();
            toast.success('Saved', { persistNotification: false });
            onSaved();
        } catch {
            toast.error('Failed to save', { persistNotification: false });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Fragment>
            <TableRow>
                <TableCell className="font-mono text-xs font-medium">
                    {entry.prompt_key}
                </TableCell>
                <TableCell>
                    <div className="flex flex-wrap gap-1">
                        {entry.available_versions.map((version) => (
                            <Badge
                                key={version}
                                variant="secondary"
                                className="font-mono"
                            >
                                {version}
                            </Badge>
                        ))}
                    </div>
                </TableCell>
                <TableCell>
                    <Select
                        value={entry.default_version}
                        onValueChange={(value) => {
                            void runSave(() =>
                                putStringFlag(
                                    entry.default_version_flag,
                                    value,
                                ),
                            );
                        }}
                    >
                        <SelectTrigger className="w-20">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {entry.available_versions.map((version) => (
                                <SelectItem key={version} value={version}>
                                    {version}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </TableCell>
                <TableCell>
                    <Select
                        value={testVersion ?? NO_TEST_VERSION}
                        onValueChange={(value) => {
                            void runSave(() =>
                                putStringFlag(
                                    entry.candidate_version_flag,
                                    value === NO_TEST_VERSION ? '' : value,
                                ),
                            );
                        }}
                    >
                        <SelectTrigger className="w-24">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={NO_TEST_VERSION}>
                                none
                            </SelectItem>
                            {entry.available_versions.map((version) => (
                                <SelectItem key={version} value={version}>
                                    {version}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </TableCell>
                <TableCell>
                    <span className="text-muted-foreground text-xs tabular-nums">
                        {testOrgCount === 1 ? '1 org' : `${testOrgCount} orgs`}
                    </span>
                    {autoOrgCount > 0 ? (
                        <div className="text-muted-foreground/70 text-xs tabular-nums">
                            {manualOrgCount} manual + {autoOrgCount} auto
                        </div>
                    ) : null}
                </TableCell>
                <TableCell>
                    <StatusBadge
                        testVersion={testVersion}
                        testOrgCount={testOrgCount}
                    />
                </TableCell>
                <TableCell>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setIsExpanded((open) => !open);
                        }}
                    >
                        {isExpanded ? 'Hide' : 'Manage'}
                    </Button>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableCell colSpan={7}>
                        <div className="flex flex-col gap-4 py-2">
                            <TestOrgsEditor entry={entry} onSaved={onSaved} />
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    disabled={!testVersion || isSaving}
                                    onClick={() => {
                                        if (!testVersion) {
                                            return;
                                        }
                                        if (
                                            !window.confirm(
                                                `Make "${testVersion}" the default for ALL organizations on ${entry.prompt_key}? Takes effect immediately — no deploy.`,
                                            )
                                        ) {
                                            return;
                                        }
                                        void runSave(() =>
                                            promotePromptCandidate(
                                                entry.prompt_key,
                                                testVersion,
                                            ),
                                        );
                                    }}
                                >
                                    Make test version the default
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={manualOrgCount === 0 || isSaving}
                                    onClick={() => {
                                        if (
                                            !window.confirm(
                                                `Remove all ${manualOrgCount} manual test org(s) from ${entry.prompt_key}? Training-mode orgs keep the test version until you clear it.`,
                                            )
                                        ) {
                                            return;
                                        }
                                        void runSave(() =>
                                            putTestOrgsFlag(
                                                entry.candidate_org_ids_flag,
                                                [],
                                            ),
                                        );
                                    }}
                                >
                                    Stop test
                                </Button>
                                <span className="text-muted-foreground text-xs">
                                    Takes effect immediately — no deploy.
                                </span>
                            </div>
                            {autoOrgCount > 0 ? (
                                <div className="text-muted-foreground text-xs">
                                    {autoOrgCount} training-mode org(s)
                                    auto-serve this test version. “Stop test”
                                    only removes manual orgs — set the Test
                                    version to “none” to stop the auto-served
                                    ones.
                                </div>
                            ) : null}
                            <QualityPanel
                                promptKey={entry.prompt_key}
                                defaultVersion={entry.default_version}
                                candidateVersion={entry.candidate_version}
                            />
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
}

function PromptRolloutPage() {
    const { data, isLoading, error, mutate } = usePromptRollout();

    if (isLoading) {
        return (
            <div className="flex justify-center p-6">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="p-6">
                <div className="text-destructive">
                    Failed to load prompt rollout.
                </div>
            </div>
        );
    }

    const testingNow = data.prompts.filter(
        (entry) =>
            Boolean(entry.candidate_version) &&
            (entry.candidate_orgs.length > 0 ||
                entry.auto_test_organization_count > 0),
    ).length;
    const testVersionsSet = data.prompts.filter((entry) =>
        Boolean(entry.candidate_version),
    ).length;
    const shortPromptLabel = (promptKey: string) =>
        promptKey.replace('general_agent.', '');
    const cohortFootprint: Array<CategoryCount> = data.prompts.map((entry) => ({
        label: shortPromptLabel(entry.prompt_key),
        count: entry.candidate_orgs.length + entry.auto_test_organization_count,
    }));
    const versionCoverage: Array<CategoryCount> = data.prompts.map((entry) => ({
        label: shortPromptLabel(entry.prompt_key),
        count: entry.available_versions.length,
    }));
    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold">Prompt Rollout</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Each listed prompt component runs one Default version that
                    all organizations get. To try a new version safely, pick a
                    Test version and add a few Test organizations — only they
                    get it. When it looks good, make the test version the
                    default for everyone. Changes to these components take
                    effect immediately, with no deploy.
                </p>
            </div>
            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
                <StatCard
                    label="Total organizations"
                    value={data.total_organizations}
                />
                <StatCard
                    label="Versioned prompts"
                    value={data.prompts.length}
                />
                <StatCard label="Currently testing" value={testingNow} />
                <StatCard label="Test versions set" value={testVersionsSet} />
                <StatCard
                    label="Training-mode orgs (auto)"
                    value={data.training_mode_organization_count}
                />
            </div>
            <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded-md border p-4">
                    <div className="text-sm font-medium">Rollout footprint</div>
                    <div className="text-muted-foreground mb-3 text-xs">
                        Organizations on a test version (manual cohort plus
                        training-mode auto), by prompt.
                    </div>
                    <CategoryBarChart data={cohortFootprint} interval={0} />
                </div>
                <div className="rounded-md border p-4">
                    <div className="text-sm font-medium">Version coverage</div>
                    <div className="text-muted-foreground mb-3 text-xs">
                        Versions available to roll out, by prompt.
                    </div>
                    <CategoryBarChart data={versionCoverage} interval={0} />
                </div>
            </div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Prompt</TableHead>
                            <TableHead>Versions</TableHead>
                            <TableHead>Default (all orgs)</TableHead>
                            <TableHead>Test version</TableHead>
                            <TableHead>Test orgs</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.prompts.map((entry) => (
                            <PromptRolloutRow
                                key={entry.prompt_key}
                                entry={entry}
                                onSaved={() => {
                                    void mutate();
                                }}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
